import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import type { DynamicListsApi } from './api'
import { accountName } from './hooks'
import { Modal } from './shared'
import type { CopyResult, CrmAccount, DynamicList } from './types'

const MAX_TARGETS = 100
const fold = (s: string) => s.toLocaleLowerCase()

/** Dynamic-list copy (CONTEXT.md): only adds, matches lists by name, one outcome per Account. */
export function CopyDialog({
  api,
  list,
  accounts,
  exclude = [],
  title,
  onClose,
  onCopied,
}: {
  api: DynamicListsApi
  list: DynamicList
  accounts: CrmAccount[]
  /** Accounts not offered (they already have this rule). */
  exclude?: string[]
  title?: string
  onClose: () => void
  onCopied: () => void
}) {
  const { t } = useTranslation('dynamicLists')
  const [q, setQ] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [enable, setEnable] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [results, setResults] = useState<CopyResult[] | null>(null)

  const candidates = useMemo(
    () => accounts.filter((a) => a.account_id !== list.account_id && !exclude.includes(a.account_id) && (a.platform || 'onlyfans') === 'onlyfans'),
    [accounts, list.account_id, exclude],
  )
  const shown = useMemo(() => {
    const query = fold(q.trim())
    return candidates
      .filter((a) => !query || [a.profile_name, a.username].some((s) => fold(s || '').includes(query)))
      .sort((a, b) => accountName(a).localeCompare(accountName(b), undefined, { sensitivity: 'base' }))
  }, [candidates, q])
  const byId = useMemo(() => new Map(accounts.map((a) => [a.account_id, a])), [accounts])

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < MAX_TARGETS ? [...s, id] : s))
  const allShown = shown.length > 0 && shown.every((a) => selected.includes(a.account_id))
  const toggleShown = () => {
    const ids = shown.map((a) => a.account_id)
    setSelected((s) => (allShown ? s.filter((id) => !ids.includes(id)) : [...new Set([...s, ...ids])].slice(0, MAX_TARGETS)))
  }

  const run = async () => {
    setBusy(true)
    setError('')
    try {
      const r = await api.copy(list.id, selected, enable)
      setResults(r.results)
      onCopied()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (results) {
    const count = (o: CopyResult['outcome']) => results.filter((r) => r.outcome === o).length
    return (
      <Modal
        title={title ?? t('copy.title', { name: list.name })}
        onClose={onClose}
        wide
        footer={
          <button type="button" className="dl-btn dl-btn--primary" onClick={onClose}>
            {t('close')}
          </button>
        }
      >
        <p className="dl-strong">{t('copy.summary', { created: count('created'), skipped: count('skipped'), failed: count('failed') })}</p>
        <ul className="dl-results">
          {results.map((r) => {
            const a = byId.get(r.account_id)
            return (
              <li key={r.account_id} className="dl-result">
                <span className={`dl-outcome dl-outcome--${r.outcome}`}>{t(`copy.outcome.${r.outcome}`)}</span>
                <span className="dl-ellipsis dl-strong">{a ? accountName(a) : r.account_id}</span>
                {r.reason && <span className="dl-muted dl-ellipsis">{r.reason}</span>}
              </li>
            )
          })}
        </ul>
      </Modal>
    )
  }

  return (
    <Modal
      title={title ?? t('copy.title', { name: list.name })}
      onClose={onClose}
      wide
      footer={
        <>
          {error && <p className="dl-error dl-foot-error">{error}</p>}
          <label className="dl-check">
            <input type="checkbox" checked={enable} onChange={(e) => setEnable(e.target.checked)} />
            {t('copy.enable')}
          </label>
          <button type="button" className="dl-btn dl-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="dl-btn dl-btn--primary" disabled={!selected.length || busy} onClick={() => void run()}>
            {busy ? t('copy.copying') : t('copy.submit')}
          </button>
        </>
      }
    >
      <p className="dl-hint">{t('copy.intro')}</p>
      <div className="dl-copy-bar">
        <label className="dl-search">
          <FeatherIcon name="search" size={14} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('copy.search')} />
        </label>
        <span className="dl-muted">{t('copy.selected', { count: selected.length })}</span>
      </div>
      <div className="dl-checklist">
        {shown.length > 0 && (
          <label className="dl-check-row dl-check-row--all">
            <input type="checkbox" checked={allShown} onChange={toggleShown} />
            <span className="dl-strong">{t('copy.all')}</span>
          </label>
        )}
        {shown.length === 0 && <p className="dl-muted dl-pad">{t('account.none')}</p>}
        {shown.map((a) => (
          <label key={a.account_id} className={`dl-check-row ${selected.includes(a.account_id) ? 'is-on' : ''}`}>
            <input type="checkbox" checked={selected.includes(a.account_id)} onChange={() => toggle(a.account_id)} />
            <span className="dl-ellipsis">{accountName(a)}</span>
            {a.username && <span className="dl-muted dl-ellipsis">@{a.username}</span>}
          </label>
        ))}
      </div>
    </Modal>
  )
}
